import {
  Droplets,
  Milk,
  ShieldAlert,
  Route,
  Clock,
  Handshake,
  Truck,
  Snowflake,
  type LucideIcon,
} from "lucide-react";

export interface Service {
  slug: string;
  title: string;
  icon: LucideIcon;
  summary: string;
  body: string[];
  /** Bulleted "what's included" points shown on the detail page (Quality-style). */
  highlights: string[];
  /** Optional supporting photo (public path) for the detail page. */
  image?: string;
  /** Hidden from the site but kept in code so it's easy to switch back on. */
  hidden?: boolean;
}

/**
 * Carrier services (PLACEHOLDER copy - confirm the real service list with BB).
 * BB is a tanker motor carrier (not a broker/3PL): loads come from brokers and
 * direct shippers; BB runs the trucks with company drivers + owner-operators.
 */
const allServices: Service[] = [
  {
    slug: "liquid-bulk",
    title: "Liquid Bulk Tanker",
    icon: Droplets,
    summary: "Stainless tanker capacity for bulk liquids, delivered in spec.",
    body: [
      "Late-model stainless trailers hauling bulk liquid freight across all 48 states and into Canada.",
      "Loaded, sealed, and delivered in spec - with live location on every load.",
    ],
    highlights: [
      "Late-model stainless steel tankers",
      "All 48 states & cross-border Canada",
      "Live ELD-fed tracking on every load",
      "Loaded, sealed, and delivered in spec",
    ],
    image: "/brand/industries/chemicals.jpg",
  },
  {
    // Hidden: BB does not offer food-grade yet. Flip `hidden` to false to restore.
    hidden: true,
    slug: "food-grade",
    title: "Food-Grade Tanker",
    icon: Milk,
    summary: "Sanitary trailers and documented washouts for food and beverage liquids.",
    body: [
      "Sanitary, food-grade equipment with documented tank washes between loads.",
      "Careful handling for edible oils, sweeteners, dairy, and beverage products.",
    ],
    highlights: [
      "Dedicated food-grade equipment",
      "Documented tank washes between loads",
      "Edible oils, sweeteners, dairy & beverage",
      "Traceability from origin to plant",
    ],
    image: "/brand/industries/food-grade.jpg",
  },
  {
    slug: "hazmat",
    title: "HazMat Transport",
    icon: ShieldAlert,
    summary: "HazMat-endorsed drivers moving placarded loads by the book.",
    body: [
      "Endorsed, trained drivers and placarded equipment for hazardous freight.",
      "Compliance handled end-to-end: documentation, routing, and emergency readiness.",
    ],
    highlights: [
      "HazMat-endorsed, trained drivers",
      "Placarded, compliant equipment",
      "Routing & documentation handled for you",
      "Emergency-response ready",
    ],
    image: "/brand/industries/industrial-gas.jpg",
  },
  {
    slug: "dry-van",
    title: "Dry Van",
    icon: Truck,
    summary: "Dry van capacity to complement our tanker network on select lanes.",
    body: [
      "Alongside our tanker fleet, we run dry van freight on select lanes for shippers who want a single carrier for more of their volume.",
      "Same professional drivers, clean paperwork, and live tracking you get on every tanker load.",
    ],
    highlights: [
      "Palletized and general dry freight",
      "Available on select lanes",
      "Live tracking on every load",
      "One carrier for more of your freight",
    ],
    image: "/brand/industries/dry-van.jpg",
  },
  {
    slug: "reefer",
    title: "Reefer",
    icon: Snowflake,
    summary: "Temperature-controlled capacity for refrigerated freight on select lanes.",
    body: [
      "Refrigerated (reefer) capacity for temperature-sensitive freight, available on select lanes.",
      "Set-point monitoring and the same on-time, in-the-loop dispatch as the rest of our fleet.",
    ],
    highlights: [
      "Temperature-controlled trailers",
      "Set-point monitored in transit",
      "Available on select lanes",
      "Live tracking on every load",
    ],
    image: "/brand/industries/reefer.jpg",
  },
  {
    slug: "dedicated-lanes",
    title: "Dedicated Lanes",
    icon: Route,
    summary: "The same trucks and drivers on your recurring lanes, week after week.",
    body: [
      "Consistent capacity committed to your repeat lanes - no re-sourcing every week.",
      "One dispatch contact who knows your facilities, products, and schedules.",
    ],
    highlights: [
      "Committed trucks on your repeat lanes",
      "One dispatch contact who knows your freight",
      "Predictable weekly capacity",
      "Consistent on-time performance",
    ],
    image: "/brand/industries/manufacturing.jpg",
  },
  {
    slug: "expedited",
    title: "Expedited Loads",
    icon: Clock,
    summary: "Time-critical tanker freight covered fast and monitored to delivery.",
    body: [
      "Direct routing and proactive monitoring against tight delivery windows.",
      "Dispatch keeps you informed before you have to ask.",
    ],
    highlights: [
      "Direct, time-critical routing",
      "Proactive monitoring to delivery",
      "Tight delivery-window coverage",
      "Updates before you have to ask",
    ],
    image: "/brand/industries/fuel-energy.jpg",
  },
  {
    slug: "broker-partnerships",
    title: "Broker Partnerships",
    icon: Handshake,
    summary: "Reliable, professional tanker capacity for freight brokers.",
    body: [
      "Quick carrier setup, clean paperwork, and live tracking shared on every load.",
      "A carrier your customers will ask for by name - on time, in spec, communicated.",
    ],
    highlights: [
      "Fast, clean carrier setup",
      "Live tracking shared on every load",
      "Professional, vetted drivers",
      "On-time, in-spec, communicated",
    ],
    image: "/brand/industries/agriculture.jpg",
  },
];

/** Visible services (hidden ones are kept in `allServices` for easy restore). */
export const services: Service[] = allServices.filter((s) => !s.hidden);

export function getService(slug: string): Service | undefined {
  return services.find((s) => s.slug === slug);
}
